import { Doughnut, Bar } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement } from 'chart.js';
import { Box, Grid, Typography, Paper, CircularProgress } from '@mui/material';
import { useGetStatusTotalByUserIdQuery } from '../apis/CartItemApi';
import { getUserData } from '../auth';
import { useGetOrderStatusTotalByUserIdQuery } from '../apis/OrderAPi';

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

const OrderStatusChartAmountCustomer = () => {
  // Extract user data from localStorage
  const userData = getUserData();
  const userId = userData?.uid ? Number(userData.uid) : 0; // Convert sellerId to a number

  // Fetch order data
  const { data: orderData = [], isLoading, isError } = useGetOrderStatusTotalByUserIdQuery({
    userId,
    isActive:false,
  });

 // console.log("orderData userId:", orderData);

  if (isLoading)
    return (
      <div
        className="flex-center py-20"
        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}
      >
        <CircularProgress sx={{ color: '#FFA500' }} />
      </div>
    );

  if (isError)
    return <Typography color="error">Failed to load data. Please try again later.</Typography>;

  if (!orderData || orderData.length === 0)
    return <Typography>No data available for you</Typography>;

  // Status color mapping
  const statusColors = {
    pending: '#FFC107',
    processing: '#17A2B8',
    received: '#28A745',
    rejected: '#DC3545',
  };

  // Doughnut Chart Data
  const doughnutData = {
    labels: orderData.map((item) => item.status?.toUpperCase() || 'UNKNOWN'),
    datasets: [
      {
        label: 'Orders',
        data: orderData.map((item) => item.count || 0),
        backgroundColor: orderData.map((item) => statusColors[item.status] || '#6C757D'),
        borderWidth: 2,
      },
    ],
  };

  // Bar Chart Data
  const barData = {
    labels: orderData.map((item) => item.status?.toUpperCase() || 'UNKNOWN'),
    datasets: [
      {
        label: 'Total Amount (USD)',
        data: orderData.map((item) => item.totalAmount || 0),
        backgroundColor: orderData.map((item) => statusColors[item.status] || '#6C757D'),
        borderWidth: 1,
      },
    ],
  };

  // Doughnut Chart Options
  const doughnutOptions = {
    plugins: {
      legend: { position: 'bottom' },
      tooltip: {
        callbacks: {
          label: (context) => {
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const value = context.raw;
            const percentage = total ? ((value / total) * 100).toFixed(1) + '%' : '0%';
            return `${context.label}: ${value} orders (${percentage})`;
          },
        },
      },
    },
  };

  // Bar Chart Options
  const barOptions = {
    indexAxis: 'y',
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context) => {
            const status = context.label.toLowerCase();
            const item = orderData.find((d) => d.status === status);
            const count = item ? item.count : 0;
            return [`Amount: $${context.raw.toFixed(2)}`, `Orders: ${count}`];
          },
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        ticks: {
          callback: (value) => `$${value}`,
        },
      },
    },
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
      <Grid container spacing={3}>
        {/* Doughnut Chart */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>
              Orders by Quantity
            </Typography>
            <Doughnut data={doughnutData} options={doughnutOptions} />
          </Paper>
        </Grid>

        {/* Bar Chart */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>
              Orders by Value
            </Typography>
            <Bar data={barData} options={barOptions} />
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OrderStatusChartAmountCustomer;
